import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../contexts/ToastContext'
import AdminLayout from '../../components/admin/AdminLayout'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import { supabase } from '../../lib/supabase'
import { Search, Filter, Plus, Edit, Trash2, Megaphone, AlertCircle, X } from 'lucide-react'

interface Campaign {
  id: string
  title: string
  description: string
  theme: string
  location: string
  target_signatures: number
  current_signatures: number
  status: string
  urgency_level: string
  is_published: boolean
  created_at: string
}

export default function CampaignsManagement() {
  const { hasPermission } = useAuth()
  const toast = useToast()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [modalOpen, setModalOpen] = useState<'create' | 'edit' | null>(null)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    campaignId: string | null
  }>({ isOpen: false, campaignId: null })

  useEffect(() => {
    loadCampaigns()
  }, [])

  const loadCampaigns = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setCampaigns(data || [])
    } catch (error: any) {
      console.error('Error loading campaigns:', error)
      toast.error('Error al cargar campañas')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!hasPermission('delete_campaign')) {
      toast.error('No tienes permisos para eliminar campañas')
      return
    }

    setConfirmDialog({ isOpen: true, campaignId })
  }

  const confirmDelete = async () => {
    if (!confirmDialog.campaignId) return

    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', confirmDialog.campaignId)

      if (error) throw error
      toast.success('Campaña eliminada exitosamente')
      loadCampaigns()
    } catch (error: any) {
      console.error('Error deleting campaign:', error)
      toast.error(`Error: ${error.message}`)
    } finally {
      setConfirmDialog({ isOpen: false, campaignId: null })
    }
  }

  const handleCreateCampaign = async (campaignData: any) => {
    if (!hasPermission('create_campaign')) {
      toast.error('No tienes permisos para crear campañas')
      return
    }

    try {
      const { error } = await supabase
        .from('campaigns')
        .insert(campaignData)

      if (error) throw error

      toast.success('Campaña creada exitosamente')
      loadCampaigns()
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error creating campaign:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleUpdateCampaign = async (campaignId: string, campaignData: any) => {
    if (!hasPermission('edit_campaign')) {
      toast.error('No tienes permisos para editar campañas')
      return
    }

    try {
      const { error } = await supabase
        .from('campaigns')
        .update(campaignData)
        .eq('id', campaignId)

      if (error) throw error

      toast.success('Campaña actualizada exitosamente')
      loadCampaigns()
      setModalOpen(null)
      setSelectedCampaign(null)
    } catch (error: any) {
      console.error('Error updating campaign:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.location?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus
    return matchesSearch && matchesStatus
  })

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Campañas</h1>
            <p className="text-gray-600">Administra las campañas cívicas de la plataforma</p>
          </div>
          {hasPermission('create_campaign') && (
            <button
              onClick={() => setModalOpen('create')}
              className="flex items-center space-x-2 px-4 py-2 bg-action text-white rounded-lg hover:bg-action/90"
            >
              <Plus className="h-4 w-4" />
              <span>Nueva Campaña</span>
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar campañas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent appearance-none"
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activas</option>
                <option value="completed">Completadas</option>
                <option value="won">Ganadas</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Mostrando {filteredCampaigns.length} de {campaigns.length} campañas
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action mx-auto"></div>
              <p className="text-gray-600 mt-2">Cargando campañas...</p>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="p-8 text-center">
              <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron campañas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaña</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{campaign.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-md">{campaign.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{campaign.location}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {campaign.current_signatures} / {campaign.target_signatures}
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-action h-2 rounded-full"
                            style={{ width: `${Math.min((campaign.current_signatures / campaign.target_signatures) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'won' ? 'bg-blue-100 text-blue-800' :
                          campaign.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {hasPermission('edit_campaign') && (
                            <button
                              onClick={() => {
                                setSelectedCampaign(campaign)
                                setModalOpen('edit')
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {hasPermission('delete_campaign') && (
                            <button
                              onClick={() => handleDeleteCampaign(campaign.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modalOpen === 'create' && (
          <CampaignModal
            onClose={() => setModalOpen(null)}
            onSave={handleCreateCampaign}
          />
        )}

        {modalOpen === 'edit' && selectedCampaign && (
          <CampaignModal
            campaign={selectedCampaign}
            onClose={() => {
              setModalOpen(null)
              setSelectedCampaign(null)
            }}
            onSave={(data) => handleUpdateCampaign(selectedCampaign.id, data)}
          />
        )}

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, campaignId: null })}
          onConfirm={confirmDelete}
          title="Eliminar Campaña"
          message="¿Estás seguro de que deseas eliminar esta campaña? Esta acción no se puede deshacer y se perderán todos los datos asociados."
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />
      </div>
    </AdminLayout>
  )
}

function CampaignModal({ campaign, onClose, onSave }: any) {
  const [formData, setFormData] = useState({
    title: campaign?.title || '',
    description: campaign?.description || '',
    theme: campaign?.theme || '',
    location: campaign?.location || '',
    target_signatures: campaign?.target_signatures || 1000,
    status: campaign?.status || 'active',
    urgency_level: campaign?.urgency_level || 'normal',
    organizer_name: campaign?.organizer_name || '',
    deadline: campaign?.deadline ? campaign.deadline.split('T')[0] : '',
    is_published: campaign?.is_published || false
  })

  const handleSubmit = () => {
    if (!formData.title || !formData.description) {
      return
    }
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {campaign ? 'Editar Campaña' : 'Crear Nueva Campaña'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tema</label>
                <input
                  type="text"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organizador</label>
                <input
                  type="text"
                  value={formData.organizer_name}
                  onChange={(e) => setFormData({ ...formData, organizer_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta de firmas</label>
                <input
                  type="number"
                  value={formData.target_signatures}
                  onChange={(e) => setFormData({ ...formData, target_signatures: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                >
                  <option value="active">Activa</option>
                  <option value="completed">Completada</option>
                  <option value="won">Ganada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Urgencia</label>
                <select
                  value={formData.urgency_level}
                  onChange={(e) => setFormData({ ...formData, urgency_level: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                >
                  <option value="low">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha límite</label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-action focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="rounded border-gray-300 text-action focus:ring-action"
              />
              <label htmlFor="is_published" className="text-sm text-gray-700">
                Publicar campaña inmediatamente
              </label>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleSubmit}
              disabled={!formData.title || !formData.description}
              className="flex-1 bg-action text-white px-4 py-2 rounded-lg hover:bg-action/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {campaign ? 'Guardar Cambios' : 'Crear Campaña'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
